/**
 * 09 §4 options screen — DOM overlay in the bracket register (open L-corner
 * brackets via pseudo-elements, neutral ink, amber for conflicts). Tabs are
 * driven by OPTION_SPECS; CONTROLS is the 05 §6 rebinding UI. Live apply via
 * OptionsStore; per-tab RESET + RESET ALL. Thin DOM glue only — all logic
 * lives in options.ts.
 */
import { OPTION_KEYS, OPTION_SPECS, bindingConflicts, type OptionKey, type OptionTab, type OptionsStore } from '../options.js'
import type { Bindings } from '../input.js'

export interface OptionsScreenCallbacks {
  onUiSound?: (id: string, bus?: string) => void
  onNameChange?: (name: string) => void
  onReplayTraining?: () => void
  onResetIdentity?: () => void
  onOpenLoadout?: () => void
  /** 10 §2 party (Sprint 7): join/create a code or leave (null); current membership for the tab. */
  onParty?: (code: string | null) => void
  getParty?: () => { code: string | null; members: string[]; error?: string }
  /** 12 §7.4 device linking (Sprint 8): issue a code here, redeem one typed from another device. */
  onLinkIssue?: () => void
  onLinkRedeem?: (code: string) => void
  getLinkCode?: () => { code: string; expiresAt: string } | null
}

const TABS: { id: OptionTab | 'controls' | 'loadout' | 'party'; label: string }[] = [
  { id: 'audio', label: 'AUDIO' }, { id: 'a11y', label: 'ACCESSIBILITY' }, { id: 'hud', label: 'HUD' }, { id: 'controls', label: 'CONTROLS' }, { id: 'video', label: 'VIDEO' }, { id: 'profile', label: 'PROFILE' }, { id: 'party', label: 'PARTY' }, { id: 'loadout', label: 'LOADOUT' },
]
const ACTION_LABELS: Record<keyof Bindings, string> = { throw: 'THROW', jump: 'JUMP', shield: 'SHIELD', dash: 'DASH', queue: 'QUEUE TOGGLE', spectate: 'SPECTATE', menu: 'MENU', camera: 'CAMERA (DEBUG)', feedback: 'FEEDBACK MARKER' }

export const OPTIONS_CSS = `
#opts{position:fixed;inset:0;display:none;place-items:center;background:rgba(11,13,16,.55);font-family:"Barlow Condensed","Oswald","Arial Narrow",sans-serif;color:#E8ECEF;z-index:20}
#opts.open{display:grid}
#opts .panel{position:relative;width:760px;max-width:94vw;max-height:88vh;overflow:auto;background:rgba(11,13,16,.94);padding:28px 32px}
#opts .br{position:relative}
#opts .br::before,#opts .br::after{content:"";position:absolute;width:18px;height:18px;border:2px solid #E8ECEF;opacity:.85}
#opts .br::before{left:0;top:0;border-right:0;border-bottom:0}
#opts .br::after{right:0;bottom:0;border-left:0;border-top:0}
#opts h1{font-size:28px;font-weight:700;letter-spacing:2px;margin:0 0 14px;text-transform:uppercase}
#opts .tabs{display:flex;gap:14px;margin-bottom:18px;flex-wrap:wrap}
#opts .tab{background:none;border:0;color:#E8ECEF;opacity:.55;font:inherit;font-size:15px;letter-spacing:1.5px;padding:6px 10px;cursor:pointer;text-transform:uppercase}
#opts .tab.on{opacity:1}
#opts .tab.on.br::before,#opts .tab.on.br::after{width:8px;height:8px}
#opts .rowo{display:grid;grid-template-columns:220px 1fr 70px;align-items:center;gap:14px;padding:7px 0;font-size:15px;letter-spacing:1px;text-transform:uppercase}
#opts .rowo .val{opacity:.7;text-align:right;font-family:"IBM Plex Mono",ui-monospace,monospace;text-transform:none;font-size:13px}
#opts input[type=range]{width:100%;accent-color:#E8ECEF}
#opts .seg{display:flex;gap:6px}
#opts .seg button,#opts .tog,#opts .btn,#opts .slot{background:none;border:0;color:#E8ECEF;font:inherit;font-size:14px;letter-spacing:1px;padding:5px 12px;cursor:pointer;opacity:.6;text-transform:uppercase;position:relative}
#opts .seg button.on,#opts .tog.on,#opts .btn,#opts .slot{opacity:1}
#opts .tog::before{content:"[ ";opacity:.5}#opts .tog::after{content:" ]";opacity:.5}
#opts .btn{border-left:2px solid #E8ECEF;border-bottom:2px solid #E8ECEF;padding-left:8px}
#opts .slot{border-left:2px solid rgba(232,236,239,.5);min-width:90px;text-align:left;font-family:"IBM Plex Mono",ui-monospace,monospace;text-transform:none;font-size:13px}
#opts .slot.conflict{color:#E0A33B;border-left-color:#E0A33B}
#opts .slot.capturing{color:#E0A33B}
#opts .slot.empty{opacity:.4}
#opts .foot{display:flex;gap:18px;margin-top:22px;justify-content:space-between;flex-wrap:wrap}
#opts input[type=text]{background:rgba(232,236,239,.06);border:0;border-bottom:1px solid #E8ECEF;color:#E8ECEF;font:inherit;font-size:15px;padding:4px 6px;width:100%}
#opts .warn{color:#E0A33B;font-size:13px;letter-spacing:1px}
#opts .note{opacity:.6;font-size:13px;letter-spacing:1px;margin:10px 0}
`

export class OptionsScreen {
  readonly root: HTMLElement
  private panel: HTMLElement
  private tab: OptionTab | 'controls' | 'loadout' | 'party' = 'audio'
  private capturing: keyof Bindings | null = null
  private captureSlot = 0
  private confirmReset = false
  private unsub: (() => void)[] = []

  constructor(private readonly store: OptionsStore, private readonly cb: OptionsScreenCallbacks = {}, doc: Document = document) {
    const style = doc.createElement('style'); style.textContent = OPTIONS_CSS; doc.head.appendChild(style)
    this.root = doc.createElement('div'); this.root.id = 'opts'
    this.panel = doc.createElement('div'); this.panel.className = 'panel br'
    this.root.appendChild(this.panel)
    doc.body.appendChild(this.root)
    this.unsub.push(store.subscribe(() => { if (this.visible) this.render() }), store.subscribeBindings(() => { if (this.visible) this.render() }))
    doc.addEventListener('keydown', (e) => this.onKey(e), true)
    doc.addEventListener('mousedown', (e) => { if (this.capturing && this.visible) { e.preventDefault(); e.stopPropagation(); this.finishCapture(`Mouse${e.button}`) } }, true)
    this.root.addEventListener('contextmenu', (e) => e.preventDefault())
  }

  get visible(): boolean { return this.root.classList.contains('open') }
  open(): void { if (this.visible) return; this.root.classList.add('open'); this.cb.onUiSound?.('ui_open'); this.render() }
  close(): void { if (!this.visible) return; this.capturing = null; this.confirmReset = false; this.root.classList.remove('open'); this.cb.onUiSound?.('ui_close') }
  toggle(): void { if (this.visible) this.close(); else this.open() }

  private onKey(e: KeyboardEvent): void {
    if (!this.visible) return
    if (this.capturing) {
      e.preventDefault(); e.stopPropagation()
      if (e.code === 'Escape') { this.capturing = null; this.cb.onUiSound?.('ui_cancel'); this.render(); return }
      if (e.code === 'Backspace') { this.setSlot(this.capturing, this.captureSlot, null); this.capturing = null; this.render(); return }
      this.finishCapture(e.code)
      return
    }
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close() }
  }

  private finishCapture(code: string): void {
    const action = this.capturing!
    this.setSlot(action, this.captureSlot, code)
    this.capturing = null
    const conflict = bindingConflicts(this.store.bindings).has(code)
    this.cb.onUiSound?.(conflict ? 'ui_bind_warn' : 'ui_bind_ok')
    this.render()
  }

  private setSlot(action: keyof Bindings, slot: number, code: string | null): void {
    const codes = this.store.bindings[action].slice()
    if (code === null) codes.splice(slot, 1)
    else if (slot < codes.length) codes[slot] = code
    else codes.push(code)
    this.store.setBinding(action, codes)
  }

  private el<K extends keyof HTMLElementTagNameMap>(tagName: K, cls?: string, txt?: string): HTMLElementTagNameMap[K] {
    const e = this.root.ownerDocument.createElement(tagName)
    if (cls) e.className = cls
    if (txt !== undefined) e.textContent = txt
    return e
  }

  render(): void {
    const p = this.panel
    p.innerHTML = ''
    p.appendChild(this.el('h1', undefined, 'OPTIONS'))
    const tabs = this.el('div', 'tabs')
    for (const t of TABS) {
      const b = this.el('button', `tab br ${this.tab === t.id ? 'on' : ''}`, t.label)
      b.onclick = () => {
        if (t.id === 'loadout') { this.cb.onUiSound?.('ui_confirm'); this.cb.onOpenLoadout?.(); this.close(); return }
        this.tab = t.id; this.capturing = null; this.cb.onUiSound?.('ui_move'); this.render()
      }
      tabs.appendChild(b)
    }
    p.appendChild(tabs)
    if (this.tab === 'controls') this.renderControls(p)
    else if (this.tab === 'party') this.renderParty(p)
    else if (this.tab !== 'loadout') this.renderTab(p, this.tab)
    const foot = this.el('div', 'foot')
    if (this.tab !== 'controls' && this.tab !== 'party') {
      const reset = this.el('button', 'btn', 'RESET TAB'); reset.onclick = () => { this.store.resetTab(this.tab as OptionTab); this.cb.onUiSound?.('ui_confirm') }
      foot.appendChild(reset)
    }
    const all = this.el('button', 'btn', this.confirmReset ? 'RESET ALL? [CONFIRM]' : 'RESET ALL')
    all.onclick = () => { if (this.confirmReset) { this.store.resetAll(); this.store.resetBindings(); this.confirmReset = false; this.cb.onUiSound?.('ui_confirm') } else { this.confirmReset = true; this.cb.onUiSound?.('ui_move') } this.render() }
    foot.appendChild(all)
    const close = this.el('button', 'btn', '[ESC] CLOSE'); close.onclick = () => this.close()
    foot.appendChild(close)
    p.appendChild(foot)
  }

  private renderTab(p: HTMLElement, tab: OptionTab): void {
    for (const key of OPTION_KEYS) {
      const spec = OPTION_SPECS[key]
      if (spec.tab !== tab || spec.kind === 'object') continue
      if (key === 'onboarding.completed') continue
      const r = this.el('div', 'rowo')
      r.appendChild(this.el('div', undefined, spec.label))
      const ctl = this.el('div')
      const val = this.el('div', 'val')
      const v = this.store.get(key)
      if (spec.kind === 'number') {
        const inp = this.el('input'); inp.type = 'range'; inp.min = String(spec.min); inp.max = String(spec.max); inp.step = String(spec.step); inp.value = String(v)
        const show = () => { val.textContent = key.startsWith('audio.') && spec.max === 1 ? String(Math.round(Number(inp.value) * 100)) : Number(inp.value).toFixed(2).replace(/\.?0+$/, '') }
        show()
        inp.oninput = () => { this.store.set(key, Number(inp.value) as never); show(); this.cb.onUiSound?.('ui_slider', key.startsWith('audio.') ? key.slice(6) : undefined) }
        ctl.appendChild(inp)
      } else if (spec.kind === 'bool') {
        const b = this.el('button', `tog ${v ? 'on' : ''}`, v ? 'ON' : 'OFF')
        b.onclick = () => { this.store.set(key, !v as never); this.cb.onUiSound?.('ui_confirm') }
        ctl.appendChild(b)
      } else if (spec.kind === 'enum') {
        const seg = this.el('div', 'seg')
        for (const opt of spec.values) { const b = this.el('button', v === opt ? 'on' : '', opt); b.onclick = () => { this.store.set(key, opt as never); this.cb.onUiSound?.('ui_confirm') }; seg.appendChild(b) }
        ctl.appendChild(seg)
      } else if (spec.kind === 'string') {
        const inp = this.el('input'); inp.type = 'text'; inp.maxLength = spec.max; inp.value = String(v); inp.placeholder = `${spec.min}–${spec.max} chars`
        const warn = this.el('div', 'warn')
        inp.onchange = () => {
          const name = inp.value.trim()
          if (name.length && (name.length < spec.min || name.length > spec.max)) { warn.textContent = `${spec.min}–${spec.max} CHARACTERS`; this.cb.onUiSound?.('ui_error'); return }
          warn.textContent = ''; this.store.set(key, name as never); this.cb.onNameChange?.(name); this.cb.onUiSound?.('ui_confirm')
        }
        ctl.appendChild(inp); ctl.appendChild(warn)
      }
      r.appendChild(ctl); r.appendChild(val)
      p.appendChild(r)
    }
    if (tab === 'profile') {
      const tr = this.el('button', 'btn', 'REPLAY TRAINING'); tr.onclick = () => { this.store.set('onboarding.completed', false); this.cb.onUiSound?.('ui_confirm'); this.cb.onReplayTraining?.(); this.close() }
      p.appendChild(tr)
      p.appendChild(this.el('div', 'note', 'training replays on your next hub spawn'))
      let confirm = false
      const ri = this.el('button', 'btn', 'RESET IDENTITY')
      ri.onclick = () => { if (confirm) { this.cb.onResetIdentity?.(); this.cb.onUiSound?.('ui_confirm'); this.close() } else { confirm = true; ri.textContent = 'RESET IDENTITY? [CONFIRM] — stats and name are lost'; this.cb.onUiSound?.('ui_bind_warn') } }
      p.appendChild(ri)
      // 12 §7.4 device linking.
      p.appendChild(this.el('div', 'note', 'LINK ANOTHER DEVICE — your level, kit and imprints follow you; each device keeps its own name.'))
      const lc = this.cb.getLinkCode?.() ?? null
      const issue = this.el('button', 'btn', lc ? `LINK CODE ${lc.code} · valid 10 min · NEW CODE` : 'LINK ANOTHER DEVICE')
      issue.onclick = () => { this.cb.onLinkIssue?.(); this.cb.onUiSound?.('ui_confirm') }
      p.appendChild(issue)
      const row = this.el('div', 'rowo'); row.appendChild(this.el('div', undefined, 'ENTER LINK CODE'))
      const input = this.el('input') as HTMLInputElement; input.type = 'text'; input.maxLength = 8; input.placeholder = 'FROM YOUR OTHER DEVICE'; input.style.textTransform = 'uppercase'
      const go = this.el('button', 'btn', 'LINK')
      const redeem = () => { const c = input.value.trim().toUpperCase(); if (/^[A-Z0-9]{8}$/.test(c)) { this.cb.onLinkRedeem?.(c); this.cb.onUiSound?.('ui_confirm') } else this.cb.onUiSound?.('ui_bind_warn') }
      go.onclick = redeem; input.onkeydown = (e) => { if (e.key === 'Enter') redeem(); e.stopPropagation() }
      row.appendChild(input); row.appendChild(go); p.appendChild(row)
    }
  }

  /** PARTY (10 §2, Sprint 7): your code, who is in it, join by code, new code, leave. Equal codes queue onto one team. */
  private renderParty(p: HTMLElement): void {
    const st = this.cb.getParty?.() ?? { code: null, members: [] }
    if (st.code) {
      p.appendChild(this.el('h1', undefined, `PARTY ${st.code}`))
      p.appendChild(this.el('div', 'note', `${st.members.length}/2 · ${st.members.join(' · ') || '—'} — flag for the queue together; you land on one team.`))
      const leave = this.el('button', 'btn', 'LEAVE PARTY'); leave.onclick = () => { this.cb.onParty?.(null); this.cb.onUiSound?.('ui_confirm'); this.render() }
      p.appendChild(leave)
    } else {
      p.appendChild(this.el('div', 'note', 'No party. Share a code with a friend: equal codes are kept on one team and never matched against each other.'))
      const fresh = this.el('button', 'btn', 'NEW CODE')
      fresh.onclick = () => { const A = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; let c = ''; for (let i = 0; i < 4; i++) c += A[Math.floor(Math.random() * A.length)]; this.cb.onParty?.(c); this.cb.onUiSound?.('ui_confirm') }
      p.appendChild(fresh)
      const row = this.el('div', 'rowo'); row.appendChild(this.el('div', undefined, 'JOIN CODE'))
      const input = this.el('input') as HTMLInputElement; input.type = 'text'; input.maxLength = 4; input.placeholder = 'AB12'; input.style.textTransform = 'uppercase'
      const join = this.el('button', 'btn', 'JOIN')
      const go = () => { const c = input.value.trim().toUpperCase(); if (/^[A-Z0-9]{4}$/.test(c)) { this.cb.onParty?.(c); this.cb.onUiSound?.('ui_confirm') } else this.cb.onUiSound?.('ui_bind_warn') }
      join.onclick = go; input.onkeydown = (e) => { if (e.key === 'Enter') go(); e.stopPropagation() }
      row.appendChild(input); row.appendChild(join); p.appendChild(row)
    }
    if (st.error) p.appendChild(this.el('div', 'warn', st.error === 'FULL' ? 'THAT PARTY IS FULL (2/2)' : 'BAD CODE — four letters or digits'))
  }

  private renderControls(p: HTMLElement): void {
    const conflicts = bindingConflicts(this.store.bindings)
    if (conflicts.size) p.appendChild(this.el('div', 'warn', `CONFLICT: ${[...conflicts].join(', ')} bound to more than one action`))
    p.appendChild(this.el('div', 'note', 'click a slot, press a key or mouse button · ESC cancels · BACKSPACE clears · physical keys (layout-independent)'))
    for (const action of Object.keys(ACTION_LABELS) as (keyof Bindings)[]) {
      const r = this.el('div', 'rowo')
      r.appendChild(this.el('div', undefined, ACTION_LABELS[action]))
      const slots = this.el('div', 'seg')
      const codes = this.store.bindings[action]
      const n = Math.min(4, codes.length + 1)
      for (let i = 0; i < n; i++) {
        const code = codes[i]
        const cap = this.capturing === action && this.captureSlot === i
        const b = this.el('button', `slot ${code && conflicts.has(code) ? 'conflict' : ''} ${cap ? 'capturing' : ''} ${code ? '' : 'empty'}`, cap ? 'PRESS A KEY…' : code ? pretty(code) : '+ add')
        b.onclick = (e) => { e.stopPropagation(); this.capturing = action; this.captureSlot = i; this.cb.onUiSound?.('ui_move'); this.render() }
        slots.appendChild(b)
      }
      r.appendChild(slots)
      p.appendChild(r)
    }
    const rs = this.el('button', 'btn', 'RESET TO DEFAULTS'); rs.onclick = () => { this.store.resetBindings(); this.cb.onUiSound?.('ui_confirm') }
    p.appendChild(rs)
  }
}

export function pretty(code: string): string {
  if (code.startsWith('Mouse')) return ['LMB', 'MMB', 'RMB'][Number(code.slice(5))] ?? code
  return code.replace(/^Key/, '').replace(/^Digit/, '').replace(/Left$/, ' L').replace(/Right$/, ' R')
}
